import { FC } from 'react';
import { Alert } from '@mui/material';
import { camelCaseToWords, getOrdinalNumber } from '../../../../helpers';
import { clientDetailTabs } from './add-edit-client';

interface IAddEditCustomErrorDisplay {
  clientDetailTabLinks: IClientDetailTabLinks[];
  errors: any;
  touched: any;
  additionalContactsErrorsAreAllNull?: boolean;
  onlyAdditionalContactsErrors?: boolean;
}

type IClientDetailTabLinks =
  | IClientDetailTabLinksWithValidation
  | IClientDetailTabLinksNoValidation;

interface IClientDetailTabLinksWithValidation {
  name: string;
  url: string;
  value: clientDetailTabs;
  validation: string;
}

interface IClientDetailTabLinksNoValidation {
  name: string;
  url: string;
  value: clientDetailTabs;
  validation?: undefined;
}

export const AddEditCustomErrorDisplay: FC<IAddEditCustomErrorDisplay> = ({
  clientDetailTabLinks,
  errors,
  touched,
  additionalContactsErrorsAreAllNull,
  onlyAdditionalContactsErrors
}) => {
  // Because of the way formik works, it will provide an ordered list of additional contacts
  // If a formfield does not have an error, that index in the array will be null.
  // This will check if all the errors are null and if the only error is additional contacts.
  // If so, we will return null and not display the error, since there actually aren't any.
  if (additionalContactsErrorsAreAllNull && onlyAdditionalContactsErrors) return null;
  return (
    <Alert severity="error" sx={{ marginBottom: theme => theme.spacing(1) }}>
      There are validation errors and/or required fields on the following tabs that need to be
      addressed:
      <ul style={{ padding: 0, margin: '.5rem 0 0 1rem' }}>
        {clientDetailTabLinks.map(tab => {
          if (errors[tab.validation]) {
            if (tab.validation === 'contactInformation') {
              const itemNameErrors = Object.keys(errors[tab.validation]);
              const itemErrors = itemNameErrors.map(errorName => {
                return `${camelCaseToWords(errorName)}: ${errors[tab.validation][errorName]}`;
              });
              return (
                <li key={tab.value}>
                  {tab.name}
                  <ul>
                    {itemErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </li>
              );
            }

            if (tab.validation === 'additionalContacts') {
              // Formik names the form array items by index, so we need to get the index of the error
              const itemNameIndexs = Object.keys(errors[tab.validation]);
              // Formik also orders items
              const itemErrors = itemNameIndexs.filter(
                errorName => errors[tab.validation][errorName]
              );

              const contactErrors = itemErrors.flatMap(indexAsName => {
                return Object.keys(errors[tab.validation][indexAsName])
                  .filter(key => touched?.[tab.validation]?.[indexAsName]?.[key])
                  .flatMap(key => {
                    return `The ${getOrdinalNumber(+indexAsName + 1)} contact's ${camelCaseToWords(
                      key
                    )}: ${errors[tab.validation][indexAsName][key]}`;
                  });
              });

              if (contactErrors.every(error => !error)) return null;
              return (
                <li key={tab.value}>
                  {tab.name}
                  <ul>
                    {contactErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </li>
              );
            }
          }
          return null;
        })}
      </ul>
    </Alert>
  );
};
